import BaseballResultsProcessor from '../entities/BaseballResultsProcessor.js';
import buildFiltersForPitchingSummary from '../filters/pitchingSummaryFilters.js';
import UniqueResultParametersProvider from './UniqueResultParametersProvider.js';
import { StatType, PitchData, ResultData, PitchingStats, PitchType, ScoringData, HitData } from '../enums/index.js';
import EnumValueProvider from './EnumValueProvider.js';
import { filterOnlyHits } from '../filters/byResultTypes.js';
import UniversalPlateDisciplineStatsCalculator from '../calculators/UniversalPlateDisciplineStatsCalculator.js'; // TODO - inject
import battedBallEventClassifier from '../classificators/battedBallEventClassifier.js';
import classifiesAsBattedBallEvent from '../classificators/plateAppearanceResult/classifiesAsBattedBallEvent.js';

const buildFiltersArray = function({
    // tagging context
    pitcherIds, pitcherHandedness, pitchResults, pitchTypes, batterIds, batterHandedness,
    // scoring context
    outs, balls, strikes, runnersOn, //runnersNotOn ?
}){
    return buildFiltersForPitchingSummary({
        pitcherIds, pitcherHandedness, pitchResults, pitchTypes, batterIds, batterHandedness,
        outs, balls, strikes, runnersOn,
    });
};

const calculatePerformanceStats = function(pitches, resultsProcessor) {
    const performanceStats = {
        [PitchingStats.PitchSpeedAvg.key]: NaN,
        [PitchingStats.PitchSpeedMax.key]: NaN,
        [PitchingStats.PitchSpinAvg.key]: NaN,
        [PitchingStats.PitchMovementHAvg.key]: NaN,
        [PitchingStats.PitchMovementVAvg.key]: NaN,
        [PitchingStats.ExtensionAvg.key]: NaN,
    };

    const resultsForPitchType = resultsProcessor.mapCollectionProperties(pitches, [
        [ResultData.Id.key],
        [ResultData.BatterTagId.key],
        ResultData.getDataPath(PitchData.Result.key),
        ResultData.getDataPath(PitchData.Speed.key),
        ResultData.getDataPath(PitchData.Spin.key),
        ResultData.getDataPath(PitchData.BreakH.key),
        ResultData.getDataPath(PitchData.BreakVInd.key),
        ResultData.getDataPath(PitchData.Extension.key),
    ]);

    const stats = resultsProcessor.calcStats(
        [StatType.Avg.key, StatType.StDev.key, StatType.Max.key], resultsForPitchType, 
        // TODO - simplify it...
        [ [ [], [ PitchData.Speed.key, PitchData.Spin.key, PitchData.BreakH.key, PitchData.BreakVInd.key, PitchData.Extension.key ] ] ], false
    );

    if (stats) {
        if (stats.hasOwnProperty(PitchData.Speed.key)){
            performanceStats[PitchingStats.PitchSpeedAvg.key] = stats[PitchData.Speed.key][StatType.Avg.key];
            performanceStats[PitchingStats.PitchSpeedMax.key] = stats[PitchData.Speed.key][StatType.Max.key];
        }

        if (stats.hasOwnProperty(PitchData.Spin.key)){
            performanceStats[PitchingStats.PitchSpinAvg.key] = stats[PitchData.Spin.key][StatType.Avg.key];
        }

        if (stats.hasOwnProperty(PitchData.BreakH.key)){
            performanceStats[PitchingStats.PitchMovementHAvg.key] = stats[PitchData.BreakH.key][StatType.Avg.key];
        }

        if (stats.hasOwnProperty(PitchData.BreakVInd.key)){
            performanceStats[PitchingStats.PitchMovementVAvg.key] = stats[PitchData.BreakVInd.key][StatType.Avg.key];
        }
        
        if (stats.hasOwnProperty(PitchData.Extension.key)){
            performanceStats[PitchingStats.ExtensionAvg.key] = stats[PitchData.Extension.key][StatType.Avg.key];
        }
    }

    return performanceStats;
};

/**
 * Calculates performance stats for pitcher, which requires some data or are solely based on hits
 * @param {*} pitches - array containing pitch results for given scope
 * @param {*} resultsProcessor 
 * @param {*} hits array with hits, which will be used to match to pitches
 * @returns 
 */
const calculateHitRelatedPerformanceStats = function(pitches, resultsProcessor, hits) {
    const performanceStats = {
        [PitchingStats.HitExitSpeedAvg.key]: NaN,
    };

    const resultsMapped = resultsProcessor.mapCollectionProperties(pitches, [
        [ResultData.Id.key],
        [ResultData.HitId.key],
        ResultData.getDataPath(PitchData.Result.key), // old scoring
        ResultData.getDataPath(ScoringData.PlateAppearanceResult.key), // new scoring
    ]);

    const battedBallsHits = [];
    // let's go through results
    for (let i = 0; i < resultsMapped.length; i++) {
        const result = resultsMapped[i];
        if (
            // sometimes we can have a batted ball, but without hit measured, in this case we need to reject this batted ball from further stats
            result.hasOwnProperty(ResultData.HitId.key)
            && result[ResultData.HitId.key]
            && isBattedBall(result) // note, this will handle new and old scoring
        ) {
            battedBallsHits.push(result[ResultData.HitId.key]);
        }
    }

    // now let's filter hits...
    const hitsForBattedBalls = resultsProcessor.filterData(hits, [
        { parameter: [ResultData.Id.key], operator: 'IN', value: battedBallsHits }
    ]);
    const mappedHitsForBattedBalls = resultsProcessor.mapCollectionProperties(hitsForBattedBalls, [
        [ResultData.Id.key],
        ResultData.getDataPath(HitData.ExitSpeed.key),
    ]);

    const stats = resultsProcessor.calcStats(
        [StatType.Avg.key, StatType.StDev.key], mappedHitsForBattedBalls, 
        // TODO - simplify it...
        [ [ [], [ HitData.ExitSpeed.key ] ] ], false
    );

    if (stats) {
        if (stats.hasOwnProperty(HitData.ExitSpeed.key)) {
            performanceStats[PitchingStats.HitExitSpeedAvg.key] = stats[HitData.ExitSpeed.key][StatType.Avg.key];
        }
    }

    return performanceStats;
};

const determineSummaryForPitcher = function(pitchType, resultsForPitchersPitchType, resultsProcessor, hits) {
    // TODO - inject
    const plateDisciplineStatsCalculator = new UniversalPlateDisciplineStatsCalculator();
    const plateDiscipline = plateDisciplineStatsCalculator.calculatePlateDiscipline(resultsForPitchersPitchType, true);

    if (!plateDiscipline) {
        // TODO - maybe log only ? we still can get speeds etc
        throw new Error('Could not calculate plate discipline stats for given set of results');
    }

    // performance stats
    const performanceStats = calculatePerformanceStats(resultsForPitchersPitchType, resultsProcessor);
    if (!performanceStats) {
        // TODO - maybe log only ? we still can get speeds etc
        throw new Error('Could not calculate performance stats for given set of results');
    }

    // performance stats which are based on hits for pitches thrown
    const performanceStatsFromHits = calculateHitRelatedPerformanceStats(resultsForPitchersPitchType, resultsProcessor, hits);
    if (!performanceStatsFromHits) {
        // TODO - maybe log only ? we still can get speeds etc
        throw new Error('Could not calculate performance stats from hits for given set of results');
    }

    const summaryForPitchType = Object.assign({
        pitchType,
        resultIds: [],
        [PitchingStats.NumberOfPitches.key]: 0,
        [PitchingStats.PitchUsage.key]: NaN,
        [PitchingStats.PitchSpeedAvg.key]: NaN,
        [PitchingStats.PitchSpinAvg.key]: NaN,
        [PitchingStats.PitchMovementHAvg.key]: NaN,
        [PitchingStats.PitchMovementVAvg.key]: NaN,
        [PitchingStats.ExtensionAvg.key]: NaN,
        [PitchingStats.KZoneRate.key]: NaN,
        [PitchingStats.HitsAllowed.key]: 0,
        [PitchingStats.Strikeouts.key]: 0,
        [PitchingStats.StrikeoutRate.key]: NaN,
        [PitchingStats.Swings.key]: 0,
        [PitchingStats.SwingRate.key]: NaN,
        [PitchingStats.ZSwingRate.key]: NaN,
        [PitchingStats.ZContactRate.key]: NaN,
        [PitchingStats.OSwingRate.key]: NaN,
        [PitchingStats.OContactRate.key]: NaN,
        [PitchingStats.Whiffs.key]: 0,
        [PitchingStats.WhiffRate.key]: NaN,
        [PitchingStats.SwingingStrikeRate.key]: NaN,
        [PitchingStats.Contacts.key]: 0,
        [PitchingStats.ContactRate.key]: NaN,
        [PitchingStats.BattersFaced.key]: 0
    }, plateDiscipline, performanceStats, performanceStatsFromHits);

    //let's go through results
    for (let i = 0; i < resultsForPitchersPitchType.length; i++) {
        const result = resultsForPitchersPitchType[i];
        summaryForPitchType.resultIds.push(result[ResultData.Id.key]);
    }

    return summaryForPitchType;
};

/**
 * Checks whether flattened result is a batted ball
 * @param {*} result having PitchData.Result.key or ScoringData.PlateAppearanceResult.key property
 * @returns bool
 */
const isBattedBall = function(result) {
    if (result.hasOwnProperty(ScoringData.PlateAppearanceResult.key) && result[ScoringData.PlateAppearanceResult.key]) {
        // news scoring
        return classifiesAsBattedBallEvent(result[ScoringData.PlateAppearanceResult.key]);
    }
    if (result.hasOwnProperty(PitchData.Result.key) && result[PitchData.Result.key]) {
        // old scoring
        return battedBallEventClassifier(result[PitchData.Result.key]);
    }

    return false;
}

const calculatePitchersPercentages = function(summaryForPitcher, resultsProcessor, resultsForPitcher) {
    // let's calculate percentages for pitches
    const pitchTypeRatios = resultsProcessor.calcStats(['ratio'], resultsProcessor.mapCollectionProperties(resultsForPitcher, [
        ResultData.getDataPath(PitchData.Classification.key),
    ]), 
    [ [ [], [PitchData.Classification.key] ] ], false);

    if (pitchTypeRatios && pitchTypeRatios.hasOwnProperty(PitchData.Classification.key)){
        const ratios = pitchTypeRatios[PitchData.Classification.key].ratio;
        if (!ratios) {
            return;
        }
        for (let j = 0; j < summaryForPitcher.pitchTypes.length; j++) {
            const pitchTypeStats = summaryForPitcher.pitchTypes[j];
            if (ratios.hasOwnProperty(pitchTypeStats.pitchType)){
                pitchTypeStats[PitchingStats.PitchUsage.key] = ratios[pitchTypeStats.pitchType].percent;
            }
        }
    }
};

const getDataForPitchingSummary = function(
    results, customResultsProcessor,
    {
        // tagging context
        pitcherIds, pitcherHandedness, pitchResults, pitchTypes, batterIds, batterHandedness,
        // scoring context
        outs, balls, strikes, runnersOn,
    } = {}
){
    let resultsProcessor = customResultsProcessor || new BaseballResultsProcessor();
    const filters = buildFiltersArray({
        pitcherIds, pitcherHandedness, pitchResults, pitchTypes, batterIds, batterHandedness,
        outs, balls, strikes, runnersOn,
    });
    // TODO - maybe optimize it ? pair with pitches etc
    const hitFilters = filterOnlyHits();
    const filteredResults = resultsProcessor.filterData(results, filters);
    const resultsPerPitcher = resultsProcessor.groupData(filteredResults, [ResultData.TagId.key]);
    const pitchersInOrder = resultsProcessor.getUniqueValues(filteredResults, [ResultData.TagId.key], false);
    const hits = resultsProcessor.filterData(results, hitFilters);
    const summaryPerPitcher = [];

    for (let p = 0; p < pitchersInOrder.length; p++) {
        const pitcherId = pitchersInOrder[p];
        const resultsForPitcher = resultsPerPitcher[pitcherId];

        const summaryForPitcher = {
            pitcherId, 
            pitchTypes: []
        };

        const resultsPerPitchType = resultsProcessor.groupData(
            resultsForPitcher, [ResultData.getDataPath(PitchData.Classification.key)],
            [], true, PitchType.Unidentified.key
        );
        for (let pitchType in resultsPerPitchType) {
            // verify against enum
            const pitchTypeEnumValue = EnumValueProvider.getValue(pitchType, PitchType, PitchType.Unidentified.key);
            if (pitchTypeEnumValue.key !== pitchType) {
                // merge with unidentified
                if (resultsPerPitchType.hasOwnProperty(pitchTypeEnumValue.key)) {
                    // merge
                    resultsPerPitchType[pitchTypeEnumValue.key] = resultsPerPitchType[pitchTypeEnumValue.key].concat(resultsPerPitchType[pitchType]);
                    continue;
                }
            }

            const summaryForPitchType = determineSummaryForPitcher(
                pitchTypeEnumValue.key, resultsPerPitchType[pitchType], resultsProcessor, hits
            );

            summaryForPitcher.pitchTypes.push(summaryForPitchType);
        }

        // include totals as well
        summaryForPitcher.summary = determineSummaryForPitcher(
            undefined, resultsForPitcher, resultsProcessor, hits
        );
        // should be safe to assume
        summaryForPitcher.summary[PitchingStats.PitchUsage.key] = 100;

        calculatePitchersPercentages(summaryForPitcher, resultsProcessor, resultsForPitcher);
        
        summaryPerPitcher.push(summaryForPitcher);
    }

    return summaryPerPitcher;
};

const listUniqueParametersForPitchingSummary = function(
    results, customResultsProcessor, {
        // tagging context
        pitcherIds, pitcherHandedness, pitchResults, pitchTypes, batterIds, batterHandedness,
        // scoring context
        outs, balls, strikes, runnersOn,
    }  = {},
    parameters,
    combinedPitchResult
){
    parameters = parameters || [
        // tagging context
        [ResultData.BatterTagId.key],
        [ResultData.TagId.key],
        ResultData.getDataPath(PitchData.Result.key),
        ResultData.getDataPath(PitchData.Classification.key),
        ResultData.getDataPath(PitchData.Mechanics.key),
        ResultData.getDataPath(PitchData.SwingHandedness.key),
        ResultData.getDataPath(PitchData.Handedness.key),
        // scoring context
        ResultData.getDataPath(PitchData.BeforePlayOuts.key),
        ResultData.getDataPath(PitchData.BeforePlayBalls.key),
        ResultData.getDataPath(PitchData.BeforePlayStrikes.key),
        ResultData.getDataPath(PitchData.BeforePlayRunnersOn.key),
    ];

    const resultsProcessor = customResultsProcessor || new BaseballResultsProcessor();
    const filters = buildFiltersArray({
        pitcherIds, pitcherHandedness, pitchResults, pitchTypes, batterIds, batterHandedness,
        outs, balls, strikes, runnersOn,
    });

    const filteredResults = resultsProcessor.filterData(results, filters);
    
    return UniqueResultParametersProvider(filteredResults, parameters, resultsProcessor, combinedPitchResult, combinedPitchResult);
};

export default getDataForPitchingSummary;

export {
    getDataForPitchingSummary, listUniqueParametersForPitchingSummary
};